/**
 * Declared-variable manifest for email templates (EMAIL_TEMPLATES_CANONICAL_SYSTEM).
 *
 * Historically a template's `{{variables}}` were an IMPLICIT, regex-derived
 * concept — nothing declared which variables a template used, whether they were
 * auto-fillable from a contact, or whether a blank value was intentional. That
 * let a template send with a variable rendered to an empty string just because
 * the contact's field happened to be blank (silent data loss), while a missing
 * key blocked the send. The two cases were indistinguishable to the author.
 *
 * The manifest makes the variable set EXPLICIT and per-template:
 *   - `source`      — 'auto' (resolvable from the contact/company/ticket via the
 *                      mappers) vs 'manual' (the sender must supply it).
 *   - `required`    — the template needs a value for this variable.
 *   - `allowEmpty`  — the AUTHOR declares that a blank value is intentional here
 *                      (design-time "explicitly wanted empty").
 *   - `defaultValue`— author-provided fallback used when nothing else resolves.
 *
 * The manifest is the single source of truth per template. It is stored in the
 * new `email_templates.variables` jsonb column, edited on the Marketing → Email
 * Templates page, and consumed by the strict resolver on every send surface.
 *
 * A template with NO stored manifest (not yet migrated, or created by an older
 * client) still resolves correctly: {@link deriveTemplateVariableManifest}
 * reconstructs an equivalent manifest from the text using the SAME rules the DB
 * backfill uses, so behavior is identical whether or not the column is populated.
 */

import { z } from "zod";
import { AUTO_FILL_VARIABLES, extractTemplateVariables } from "./mappers";

/** One declared template variable. */
export interface TemplateVariableManifestEntry {
  /** The placeholder token exactly as it appears between `{{` and `}}` (trimmed). */
  key: string;
  /** Human-readable label shown in the authoring UI and variable inputs. */
  label: string;
  /** Whether the value is auto-resolvable from contact/company/ticket data. */
  source: "auto" | "manual";
  /** The template needs a value for this variable. */
  required: boolean;
  /** The author declares a blank value is intentional (explicit empty at design time). */
  allowEmpty: boolean;
  /** Author-provided fallback used when nothing else resolves; null = none. */
  defaultValue: string | null;
}

export type TemplateVariableManifest = TemplateVariableManifestEntry[];

export const templateVariableManifestEntrySchema = z.object({
  key: z.string().min(1),
  label: z.string(),
  source: z.enum(["auto", "manual"]),
  required: z.boolean(),
  allowEmpty: z.boolean(),
  defaultValue: z.string().nullable(),
});

export const templateVariableManifestSchema = z.array(templateVariableManifestEntrySchema);

/** Turn a snake_case / lower token into a Title Case label ("first_name" → "First Name"). */
export function humanizeVariableKey(key: string): string {
  return key
    .trim()
    .replace(/[_\s]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** Normalize a raw placeholder token so it matches an AUTO_FILL_VARIABLES entry. */
function normalizeKey(key: string): string {
  return key.trim().toLowerCase().replace(/\s+/g, "_");
}

/** True when a placeholder token is auto-resolvable from contact/ticket data. */
export function isAutoFillToken(token: string): boolean {
  const trimmed = token.trim();
  return AUTO_FILL_VARIABLES.has(trimmed) || AUTO_FILL_VARIABLES.has(normalizeKey(trimmed));
}

/**
 * Build the DEFAULT manifest entry for a placeholder token when the template has
 * no authored override for it. This is the parity point between three consumers:
 * the strict resolver's fallback, the create/edit persistence, and the SQL
 * backfill — all must classify a bare `{{token}}` identically.
 *
 * Rules: `source` = 'auto' iff the token is in AUTO_FILL_VARIABLES; every derived
 * variable is `required`, not `allowEmpty`, with no default. Authors relax these
 * (mark optional / allow empty / add a default) in the UI; that authored state
 * is what the `variables` column persists.
 */
export function defaultManifestEntry(token: string): TemplateVariableManifestEntry {
  return {
    key: token.trim(),
    label: humanizeVariableKey(token),
    source: isAutoFillToken(token) ? "auto" : "manual",
    required: true,
    allowEmpty: false,
    defaultValue: null,
  };
}

/**
 * Reconstruct a full manifest from a template's subject + body by scanning for
 * `{{tokens}}` and classifying each with {@link defaultManifestEntry}. Used as
 * the resolver fallback for un-migrated templates and as the seed the authoring
 * UI edits.
 */
export function deriveTemplateVariableManifest(
  subject: string,
  body: string
): TemplateVariableManifest {
  const tokens = extractTemplateVariables(`${subject}\n${body}`);
  return tokens.map(defaultManifestEntry);
}

/**
 * Merge an authored manifest with the tokens actually present in the text so the
 * result is always complete and never stale:
 *   - tokens in the text with no authored entry get a derived default entry;
 *   - authored entries whose token no longer appears in the text are dropped
 *     (prevents an orphaned "required" var blocking a send forever).
 * Order follows the text's token order for stable UI rendering.
 */
export function reconcileManifestWithText(
  subject: string,
  body: string,
  authored: TemplateVariableManifest | null | undefined
): TemplateVariableManifest {
  const tokens = extractTemplateVariables(`${subject}\n${body}`);
  const authoredByKey = new Map<string, TemplateVariableManifestEntry>();
  for (const entry of authored ?? []) authoredByKey.set(entry.key.trim(), entry);
  return tokens.map((token) => authoredByKey.get(token.trim()) ?? defaultManifestEntry(token));
}

/** Parse an unknown jsonb value into a manifest, or null when absent/invalid. */
export function parseStoredManifest(value: unknown): TemplateVariableManifest | null {
  if (value === null || value === undefined) return null;
  const parsed = templateVariableManifestSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
